/*
 * LumenForge sign-in / sign-up modal.
 *
 * Style: matches the menu drawer — solid white panel, F logo, thin divider,
 * uppercase eyebrow. NO "worlds worth keeping" copy or serif italics — that
 * was a competitor's brand, not ours.
 *
 * Auth options offered here:
 *   1. Continue with Google (Emergent-managed OAuth → redirects out and
 *      returns to /auth/shop-callback).
 *   2. Email + password (JWT stored in localStorage, no redirect).
 */
import { useEffect, useRef, useState } from "react";
import { X, Mail, Key, ArrowRight, Loader2 } from "lucide-react";
import { useShopAuth } from "../../context/ShopAuthContext";

const BRAND_BLUE = "#3b82f6";
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function SignInModal() {
  const { modalOpen, modalMode, setModalMode, closeSignIn, signIn, signUp, signInWithGoogle } = useShopAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState(null);
  const emailRef = useRef(null);

  useEffect(() => {
    if (!modalOpen) return;
    setError(null);
    setTimeout(() => emailRef.current?.focus(), 40);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [modalOpen, modalMode]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeSignIn(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeSignIn]);

  if (!modalOpen) return null;

  const isSignUp = modalMode === "signup";
  const canSubmit = /^\S+@\S+\.\S+$/.test(email) && password.length >= 6 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      if (isSignUp) await signUp(email, password, name || undefined);
      else await signIn(email, password);
      setEmail(""); setPassword(""); setName("");
      closeSignIn();
    } catch (err) {
      setError(err.message || String(err));
    } finally { setSubmitting(false); }
  };

  const onGoogleClick = async () => {
    setError(null); setGoogleBusy(true);
    try {
      await signInWithGoogle();
      // Modal state resets on success
      setEmail(""); setPassword(""); setName("");
      closeSignIn();
    } catch (e) {
      setError(e.message || String(e));
    } finally { setGoogleBusy(false); }
  };

  return (
    <div
      data-testid="signin-modal"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) closeSignIn(); }}
      style={{
        // Sign-in must sit ABOVE every product modal, cart drawer and
        // page overlay — anything the user clicked to trigger auth. The
        // product/detail modal uses zIndex 1200, the cart drawer sits at
        // 1000, so 9999 keeps sign-in unambiguously on top forever.
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(6, 10, 24, 0.55)",
        backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        animation: "signin-fade 0.22s ease-out",
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          maxHeight: "min(calc(100dvh - 40px), 740px)",
          overflow: "auto",
          background: "#ffffff",
          borderRadius: 18,
          boxShadow: "0 30px 90px rgba(6,10,24,0.35)",
          border: "1px solid rgba(0,0,0,0.06)",
          fontFamily: FONT, color: "#111",
          position: "relative",
          animation: "signin-pop 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <button
          data-testid="signin-close"
          onClick={closeSignIn}
          aria-label="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            width: 32, height: 32, borderRadius: 8,
            background: "transparent", border: "1px solid rgba(0,0,0,0.08)",
            color: "#666", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        ><X size={15} /></button>

        {/* Brand header — same treatment as the menu drawer */}
        <div style={{
          padding: "40px 32px 20px",
          display: "flex", flexDirection: "column", alignItems: "center",
          textAlign: "center",
        }}>
          <img
            src="/lumenforge-f-logo.webp"
            alt="LumenForge-MC"
            style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 12 }}
          />
          <div style={{ fontSize: "1.15rem", fontWeight: 500, color: "#111", letterSpacing: "0.005em" }}>
            LumenForge-MC
          </div>
          <div style={{
            fontSize: "0.56rem", color: "rgba(0,0,0,0.42)",
            letterSpacing: "0.42em", textTransform: "uppercase",
            marginTop: 8,
          }}>Build · Cut · Ship</div>
          <div style={{ width: "60%", height: 1, background: "rgba(0,0,0,0.1)", marginTop: 20 }} />
        </div>

        {/* Form */}
        <div style={{ padding: "8px 32px 32px" }}>
          <p style={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(0,0,0,0.5)", letterSpacing: "0.28em", textTransform: "uppercase", margin: "0 0 6px", textAlign: "center" }}>
            {isSignUp ? "Create Account" : "Sign In"}
          </p>
          <h1 style={{
            fontSize: "1.35rem", fontWeight: 500, letterSpacing: "-0.005em",
            margin: "0 0 8px", lineHeight: 1.2, textAlign: "center", color: "#111",
          }}>
            {isSignUp ? "One free account." : "Welcome back."}
          </h1>
          <p style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.82rem", margin: "0 0 22px", textAlign: "center", lineHeight: 1.5 }}>
            {isSignUp
              ? "Every future download stays with you. No spam, ever."
              : "Access your purchases and permanent library."}
          </p>

          <button
            data-testid="signin-google"
            type="button"
            onClick={onGoogleClick}
            disabled={submitting || googleBusy}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 10,
              background: "#ffffff", color: "#111",
              border: "1px solid rgba(0,0,0,0.14)",
              fontSize: "0.9rem", fontWeight: 600, fontFamily: "inherit",
              cursor: (submitting || googleBusy) ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
              transition: "border-color 0.2s ease, background 0.2s ease",
            }}
            onMouseEnter={(e) => { if (submitting || googleBusy) return; e.currentTarget.style.borderColor = "rgba(0,0,0,0.32)"; e.currentTarget.style.background = "rgba(0,0,0,0.02)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.14)"; e.currentTarget.style.background = "#ffffff"; }}
          >
            {googleBusy
              ? <><Loader2 size={14} className="spin" /> Waiting for Google…</>
              : <><GoogleGlyph /> Continue with Google</>
            }
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 18px" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
            <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(0,0,0,0.42)", letterSpacing: "0.24em", textTransform: "uppercase" }}>or</span>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
          </div>

          <form onSubmit={submit} noValidate>
            {isSignUp && (
              <Field label="Display name (optional)">
                <input
                  data-testid="signin-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jamie"
                  style={inputStyle}
                />
              </Field>
            )}
            <Field label="Email">
              <div style={{ position: "relative" }}>
                <Mail size={14} color="rgba(0,0,0,0.35)" style={{ position: "absolute", left: 12, top: 13, pointerEvents: "none" }} />
                <input
                  ref={emailRef}
                  data-testid="signin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  style={{ ...inputStyle, paddingLeft: 36 }}
                />
              </div>
            </Field>
            <Field label="Password">
              <div style={{ position: "relative" }}>
                <Key size={14} color="rgba(0,0,0,0.35)" style={{ position: "absolute", left: 12, top: 13, pointerEvents: "none" }} />
                <input
                  data-testid="signin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isSignUp ? "At least 6 characters" : "••••••••"}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  minLength={6}
                  style={{ ...inputStyle, paddingLeft: 36 }}
                />
              </div>
            </Field>

            {error && (
              <div data-testid="signin-error" style={{
                background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.28)",
                borderRadius: 8, padding: "9px 12px", fontSize: "0.82rem", color: "#b91c1c",
                marginBottom: 12,
              }}>{error}</div>
            )}

            <button
              data-testid="signin-submit"
              type="submit"
              disabled={!canSubmit}
              style={{
                width: "100%", padding: "12px 18px", borderRadius: 10,
                background: canSubmit ? BRAND_BLUE : "rgba(59,130,246,0.35)",
                color: "#fff", border: `1px solid ${canSubmit ? BRAND_BLUE : "rgba(59,130,246,0.35)"}`,
                fontSize: "0.72rem", fontWeight: 700,
                letterSpacing: "0.18em", textTransform: "uppercase",
                cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                marginTop: 4,
              }}
            >
              {submitting
                ? <><Loader2 size={13} className="spin" /> Please wait…</>
                : <>{isSignUp ? "Create account" : "Sign in"} <ArrowRight size={13} /></>
              }
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: 18, fontSize: "0.82rem", color: "rgba(0,0,0,0.55)" }}>
            {isSignUp ? (
              <>Already have an account?{" "}
                <button data-testid="signin-toggle-signin" type="button" onClick={() => setModalMode("signin")}
                  style={toggleStyle}>Sign in</button>
              </>
            ) : (
              <>Don't have an account?{" "}
                <button data-testid="signin-toggle-signup" type="button" onClick={() => setModalMode("signup")}
                  style={toggleStyle}>Sign up</button>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes signin-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes signin-pop { from { opacity: 0; transform: translateY(6px) scale(0.985); } to { opacity: 1; transform: none; } }
        .spin { animation: spin-anim 1s linear infinite; }
        @keyframes spin-anim { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// --- Google multi-colour glyph (inline SVG) ---
function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.12)",
  background: "#ffffff",
  color: "#111",
  fontSize: "0.9rem",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};
const toggleStyle = {
  background: "transparent", border: "none", padding: 0,
  color: BRAND_BLUE, cursor: "pointer",
  fontSize: "0.82rem", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3,
  fontFamily: "inherit",
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: "0.58rem", fontWeight: 700,
        color: "rgba(0,0,0,0.5)", letterSpacing: "0.28em", textTransform: "uppercase",
        marginBottom: 8,
      }}>{label}</label>
      {children}
    </div>
  );
}
